// Performance monitoring and memory usage tracking for snapshot system
import { ApplicationSnapshot } from '../types/applicationState';

export interface SnapshotMetrics {
  timestamp: number;
  snapshotId: string;
  size: {
    totalBytes: number;
    projectDataBytes: number;
    viewStatesBytes: number;
    uiStateBytes: number;
    metadataBytes: number;
  };
  performance: {
    serializationTimeMs: number;
    cloneTimeMs: number;
    compressionTimeMs?: number;
    totalTimeMs: number;
  };
  memoryUsage: {
    heapUsed: number;
    heapTotal: number;
    percentage: number;
  };
}

export interface PerformanceReport {
  totalSnapshots: number;
  totalMemoryUsage: number;
  averageSnapshotSize: number;
  averageSerializationTime: number;
  memoryEfficiency: number;
  recommendations: string[];
}

interface BrowserMemoryInfo {
  usedJSHeapSize?: number;
  totalJSHeapSize?: number;
}

export class SnapshotPerformanceMonitor {
  private metrics: SnapshotMetrics[] = [];
  private maxMetrics: number = 1000; // Keep last 1000 measurements

  /**
   * Measure snapshot creation performance
   */
  async measureSnapshotCreation<T>(
    snapshotId: string,
    snapshot: ApplicationSnapshot,
    operation: () => T
  ): Promise<{ result: T; metrics: SnapshotMetrics }> {
    const startTime = performance.now();

    // Measure serialization time
    const serializationStart = performance.now();
    const serialized = JSON.stringify(snapshot);
    const serializationTime = performance.now() - serializationStart;

    // Measure clone time
    const cloneStart = performance.now();
    const result = operation();
    const cloneTime = performance.now() - cloneStart;

    const totalTime = performance.now() - startTime;
    const endMemory = this.getCurrentMemoryUsage();

    // Calculate sizes
    const totalBytes = new Blob([serialized]).size;
    const projectDataBytes = new Blob([JSON.stringify(snapshot.projectData)]).size;
    const viewStatesBytes = new Blob([JSON.stringify(snapshot.viewStates)]).size;
    const uiStateBytes = new Blob([JSON.stringify(snapshot.uiState)]).size;
    const metadataBytes = new Blob([JSON.stringify(snapshot.metadata)]).size;

    const metrics: SnapshotMetrics = {
      timestamp: Date.now(),
      snapshotId,
      size: {
        totalBytes,
        projectDataBytes,
        viewStatesBytes,
        uiStateBytes,
        metadataBytes
      },
      performance: {
        serializationTimeMs: serializationTime,
        cloneTimeMs: cloneTime,
        totalTimeMs: totalTime
      },
      memoryUsage: {
        heapUsed: endMemory.used,
        heapTotal: endMemory.total,
        percentage: endMemory.total > 0 ? (endMemory.used / endMemory.total) * 100 : 0
      }
    };

    this.addMetrics(metrics);
    return { result, metrics };
  }

  /**
   * Get current memory usage
   */
  private getCurrentMemoryUsage(): { used: number; total: number } {
    const browserPerformance = performance as Performance & { memory?: BrowserMemoryInfo };
    if (browserPerformance.memory) {
      // Chrome/Edge have performance.memory
      const memory = browserPerformance.memory;
      return {
        used: memory.usedJSHeapSize || 0,
        total: memory.totalJSHeapSize || 0
      };
    }
    
    // Fallback estimation for other browsers
    return {
      used: 0,
      total: 0
    };
  }

  /**
   * Add metrics to collection
   */
  private addMetrics(metrics: SnapshotMetrics): void {
    this.metrics.push(metrics);
    
    // Trim old metrics if we exceed the limit
    if (this.metrics.length > this.maxMetrics) {
      this.metrics = this.metrics.slice(-this.maxMetrics);
    }
  }

  /**
   * Get comprehensive performance report
   */
  getPerformanceReport(): PerformanceReport {
    if (this.metrics.length === 0) {
      return {
        totalSnapshots: 0,
        totalMemoryUsage: 0,
        averageSnapshotSize: 0,
        averageSerializationTime: 0,
        memoryEfficiency: 100,
        recommendations: ['No data available yet']
      };
    }

    const totalSnapshots = this.metrics.length;
    const totalMemoryUsage = this.metrics.reduce((sum, m) => sum + m.size.totalBytes, 0);
    const averageSnapshotSize = totalMemoryUsage / totalSnapshots;
    const averageSerializationTime = this.metrics.reduce((sum, m) => sum + m.performance.serializationTimeMs, 0) / totalSnapshots;
    
    // Calculate memory efficiency (lower is better)
    const maxMemoryUsage = Math.max(...this.metrics.map(m => m.memoryUsage.percentage));
    const memoryEfficiency = 100 - maxMemoryUsage;

    const recommendations = this.generateRecommendations(averageSnapshotSize, averageSerializationTime, maxMemoryUsage);

    return {
      totalSnapshots,
      totalMemoryUsage,
      averageSnapshotSize,
      averageSerializationTime,
      memoryEfficiency,
      recommendations
    };
  }

  /**
   * Generate performance recommendations
   */
  private generateRecommendations(
    avgSize: number,
    avgTime: number,
    maxMemory: number
  ): string[] {
    const recommendations: string[] = [];

    // Size recommendations
    if (avgSize > 1024 * 1024) { // > 1MB
      recommendations.push('🔴 Large snapshot size detected. Consider implementing delta compression.');
    } else if (avgSize > 512 * 1024) { // > 512KB
      recommendations.push('🟡 Moderate snapshot size. Monitor for growth.');
    } else if (avgSize < 100 * 1024) { // < 100KB
      recommendations.push('🟢 Good snapshot size efficiency.');
    }

    // Performance recommendations
    if (avgTime > 50) { // > 50ms
      recommendations.push('🔴 Slow snapshot creation. Consider background processing.');
    } else if (avgTime > 20) { // > 20ms
      recommendations.push('🟡 Moderate snapshot creation time.');
    } else {
      recommendations.push('🟢 Fast snapshot creation performance.');
    }

    // Memory recommendations
    if (maxMemory > 80) {
      recommendations.push('🔴 High memory usage detected. Consider reducing snapshot retention.');
    } else if (maxMemory > 60) {
      recommendations.push('🟡 Moderate memory usage. Monitor for trends.');
    } else {
      recommendations.push('🟢 Good memory efficiency.');
    }

    // General recommendations
    if (this.metrics.length > 50) {
      const recentMetrics = this.metrics.slice(-10);
      const oldMetrics = this.metrics.slice(0, 10);
      
      const recentAvgSize = recentMetrics.reduce((sum, m) => sum + m.size.totalBytes, 0) / recentMetrics.length;
      const oldAvgSize = oldMetrics.reduce((sum, m) => sum + m.size.totalBytes, 0) / oldMetrics.length;
      
      if (recentAvgSize > oldAvgSize * 1.2) {
        recommendations.push('📈 Snapshot size is growing. Check for memory leaks.');
      }
    }

    return recommendations;
  }

  /**
   * Get metrics for specific time range
   */
  getMetricsInRange(startTime: number, endTime: number): SnapshotMetrics[] {
    return this.metrics.filter(m => 
      m.timestamp >= startTime && m.timestamp <= endTime
    );
  }

  /**
   * Get size breakdown for latest snapshots
   */
  getSizeBreakdown(count: number = 10): Array<{
    snapshotId: string;
    timestamp: number;
    sizes: SnapshotMetrics['size'];
    percentages: {
      projectData: number;
      viewStates: number;
      uiState: number;
      metadata: number;
    };
  }> {
    const recentMetrics = this.metrics.slice(-count);
    
    return recentMetrics.map(metric => ({
      snapshotId: metric.snapshotId,
      timestamp: metric.timestamp,
      sizes: metric.size,
      percentages: {
        projectData: (metric.size.projectDataBytes / metric.size.totalBytes) * 100,
        viewStates: (metric.size.viewStatesBytes / metric.size.totalBytes) * 100,
        uiState: (metric.size.uiStateBytes / metric.size.totalBytes) * 100,
        metadata: (metric.size.metadataBytes / metric.size.totalBytes) * 100
      }
    }));
  }

  /**
   * Clear all metrics (useful for testing)
   */
  clearMetrics(): void {
    this.metrics = [];
  }

  /**
   * Export metrics for analysis
   */
  exportMetrics(): string {
    return JSON.stringify({
      exportTime: new Date().toISOString(),
      totalMetrics: this.metrics.length,
      metrics: this.metrics,
      report: this.getPerformanceReport()
    }, null, 2);
  }
}

// Global instance
export const snapshotPerformanceMonitor = new SnapshotPerformanceMonitor();
